import React, { useEffect, useState } from 'react';
import Navbar from "./Navbar";
import Footer from './Footer';
import "./BookingServices.css";

const BookingServices = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
    designation: '',
    eventType: '',
    attendees: '',
    preferredDate: '',
    preferredCity: '',
    duration: 'Half Day',
    timeOfDay: '',
    catering: '',
    dietary: '',
    venuePref: [],
    requirements: '',
    source: '',
    consent: false,
    updates: false
  });
  const [formStatus, setFormStatus] = useState({ status: '', message: '' });

  // Services data
  const servicesList = [
    { icon: 'bi-calendar-check', label: 'Strategic Planning', value: 'Strategic Planning' },
    { icon: 'bi-geo-alt', label: 'Venue Selection', value: 'Venue Selection' },
    { icon: 'bi-mic', label: 'Speaker Management', value: 'Speaker Management' },
    { icon: 'bi-tv', label: 'AV Production', value: 'AV Production' },
    { icon: 'bi-cup-hot', label: 'Catering', value: 'Catering' },
    { icon: 'bi-brush', label: 'Decor & Design', value: 'Decor' },
    { icon: 'bi-camera', label: 'Photography', value: 'Photography' },
    { icon: 'bi-broadcast', label: 'Live Streaming', value: 'Live Streaming' }
  ];

  // Features data
  const features = [
    { icon: 'bi-clock-history', title: '24/7 Concierge', desc: 'Round-the-clock dedicated support' },
    { icon: 'bi-file-earmark-check', title: 'Free Consultation', desc: 'Expert advice with no obligation' },
    { icon: 'bi-pencil-square', title: 'Custom Proposals', desc: 'Tailored to your unique needs' },
    { icon: 'bi-shield-check', title: '100% Transparency', desc: 'No hidden costs, ever' }
  ];

  // Testimonials data
  const testimonials = [
    {
      text: "The team at MEDIAWAGON transformed our annual conference into an unforgettable experience. Flawless execution!",
      name: "Rajiv Mehta",
      title: "CEO, TechCorp India",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
    },
    {
      text: "Exceptional attention to detail and creative concepts. They exceeded all our expectations!",
      name: "Priya Sharma",
      title: "CMO, Innovate Inc",
      image: "https://images.unsplash.com/photo-1494790108777-466fd103c8ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "What is the booking process?",
      answer: "Simply fill out the booking form with your event details. Our team will review your requirements and contact you within 2 hours to discuss your event in detail and provide a customized proposal."
    },
    {
      question: "How soon can you respond to my enquiry?",
      answer: "We respond to all booking enquiries within 2 hours during business hours (Monday-Friday, 9AM-7PM) and within 24 hours for after-hours and weekend submissions. Premium clients receive priority response."
    },
    {
      question: "Do you require an advance payment?",
      answer: "Yes, we require a 25% advance payment to confirm and block your event dates. The remaining balance can be paid as per a mutually agreed payment schedule before the event. Flexible payment plans available."
    },
    {
      question: "Can I modify my booking after submission?",
      answer: "Absolutely! Your dedicated event manager will work with you to make any changes or adjustments to your event plan. We're flexible and adapt to your evolving requirements."
    },
    {
      question: "Do you handle events outside Hyderabad?",
      answer: "Yes, we manage events across India and internationally. Our team has experience coordinating events in multiple cities and can handle logistics regardless of location. We have partner networks in all major cities."
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'venuePref') {
        setFormData(prev => ({
          ...prev,
          venuePref: checked 
            ? [...prev.venuePref, value]
            : prev.venuePref.filter(item => item !== value)
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleService = (serviceValue) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceValue)) {
        return prev.filter(s => s !== serviceValue);
      } else {
        return [...prev, serviceValue];
      }
    });
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Function to generate WhatsApp message from form data
  const generateWhatsAppMessage = () => {
    const servicesText = selectedServices.length > 0 
      ? selectedServices.join(', ') 
      : 'Not specified';
    
    const venuePrefText = formData.venuePref.length > 0 
      ? formData.venuePref.join(', ') 
      : 'Not specified';
    
    const message = `*MEDIAWAGON - New Booking Enquiry*

*Contact Information:*
• Name: ${formData.customerName || 'Not provided'}
• Company: ${formData.companyName || 'Not provided'}
• Email: ${formData.email || 'Not provided'}
• Phone: ${formData.phoneNumber || 'Not provided'}
• Designation: ${formData.designation || 'Not provided'}

*Event Information:*
• Event Type: ${formData.eventType || 'Not provided'}
• Attendees: ${formData.attendees || 'Not provided'}
• Preferred Date: ${formData.preferredDate || 'Not provided'}
• Preferred City: ${formData.preferredCity || 'Not provided'}
• Duration: ${formData.duration || 'Not provided'}
• Time of Day: ${formData.timeOfDay || 'Not provided'}

*Services Required:*
${servicesText}

*Catering Preferences:*
• Catering Type: ${formData.catering || 'Not provided'}
• Dietary Restrictions: ${formData.dietary || 'None'}

*Venue Preferences:*
${venuePrefText}

*Event Requirements:*
${formData.requirements || 'Not provided'}

*How did they hear about us:*
${formData.source || 'Not provided'}

---
*This enquiry was submitted from MEDIAWAGON website booking form.*
*Please respond within 2 hours during business hours.*`;

    return encodeURIComponent(message);
  };

  // Reset form function
  const resetForm = () => {
    setFormData({
      customerName: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      designation: '',
      eventType: '',
      attendees: '',
      preferredDate: '',
      preferredCity: '',
      duration: 'Half Day',
      timeOfDay: '',
      catering: '',
      dietary: '',
      venuePref: [],
      requirements: '',
      source: '',
      consent: false,
      updates: false
    });
    setSelectedServices([]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    console.log("========== FORM SUBMISSION STARTED ==========");
    console.log("Form data:", formData);
    console.log("Selected services:", selectedServices);
    
    // Check consent
    if (!formData.consent) {
      setFormStatus({ 
        status: 'error', 
        message: 'Please agree to the data consent to proceed.' 
      });
      setTimeout(() => {
        setFormStatus({ status: '', message: '' });
      }, 3000);
      return;
    }

    // Check required fields
    if (!formData.customerName || !formData.companyName || !formData.email || 
        !formData.phoneNumber || !formData.eventType || !formData.attendees || 
        !formData.preferredDate || !formData.requirements) {
      setFormStatus({ 
        status: 'error', 
        message: 'Please fill in all required fields marked with *' 
      });
      setTimeout(() => {
        setFormStatus({ status: '', message: '' });
      }, 3000);
      return;
    }

    // Show success alert
    alert("✓ Your information has been sent successfully!\n\nWe are now redirecting you to WhatsApp to confirm your booking details.");
    
    // Generate WhatsApp message and create link
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/918688365247?text=${message}`;
    
    // Create and click a hidden link (most reliable method)
    const link = document.createElement('a');
    link.href = whatsappUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Reset the form after submission
    resetForm();
    
    // Show success message in the form
    setFormStatus({ 
      status: 'success', 
      message: 'Enquiry sent successfully! WhatsApp will open shortly.' 
    });
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setFormStatus({ status: '', message: '' });
    }, 5000);
  };

  const styles = {
    // Page Container with Black Borders
    pageContainer: {
      background: '#111',
      padding: 0,
      margin: 0,
      width: '100%'
    },
    mainContainer: {
      background: '#f5f5f5',
      margin: '5px auto',
      width: '98%',
      maxWidth: '1400px',
      padding: '20px',
      border: '2px solid #fff',
      borderRadius: '0px',
      boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
      marginTop: 0,
borderLeft: '11px solid #000',
borderRight: '11px solid #000'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px'
    },

    // Hero Section
    heroHeader: {
      position: 'relative',
      padding: '120px 20px 80px',
      color: 'white',
      textAlign: 'center',
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      background: 'url("https://i.pinimg.com/736x/db/56/88/db568833f1f7f236d0e5402954934886.jpg") no-repeat center center',
      backgroundSize: 'cover'
    },
    breadcrumb: {
      display: 'flex',
      justifyContent: 'center',
      gap: '0.5rem',
      marginBottom: '2rem'
    },
    breadcrumbLink: {
      color: 'rgba(255,255,255,0.9)',
      textDecoration: 'none',
      padding: '0.3rem 1rem',
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '30px',
      fontSize: '0.9rem',
      fontWeight: 500
    },
    breadcrumbActive: {
      color: 'white',
      padding: '0.3rem 1rem',
      background: 'rgba(255,255,255,0.25)',
      borderRadius: '30px',
      fontSize: '0.9rem',
      fontWeight: 600
    },
    heroBadge: {
      display: 'inlineFlex',
      alignItems: 'center',
      gap: '0.5rem',
      background: 'rgba(255,255,255,0.15)',
      padding: '0.6rem 1.5rem',
      borderRadius: '100px',
      marginBottom: '2rem',
      border: '1px solid rgba(255,255,255,0.3)',
      color: 'white',
      fontWeight: 500
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
      fontWeight: 800,
      marginBottom: '1.5rem',
      lineHeight: 1.2,
      color: 'white'
    },
    highlight: {
      color: '#FFD700',
      fontWeight: 800
    },
    heroText: {
      fontSize: '1.2rem',
      maxWidth: '600px',
      margin: '0 auto 2rem',
      opacity: 0.95,
      color: 'white'
    },
    heroStats: {
      display: 'flex',
      justifyContent: 'center',
      gap: '3rem',
      marginBottom: '2.5rem',
      flexWrap: 'wrap'
    },
    statItem: {
      textAlign: 'center'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 800,
      color: 'white',
      lineHeight: 1
    },
    statLabel: {
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      opacity: 0.9,
      color: 'white'
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    btnPrimary: {
      background: 'white',
      color: '#2563eb',
      border: 'none',
      borderRadius: '12px',
      padding: '0.9rem 2.5rem',
      fontWeight: 600,
      fontSize: '0.95rem',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
    },
    btnOutline: {
      border: '2px solid white',
      color: 'white',
      borderRadius: '12px',
      padding: '0.9rem 2.5rem',
      fontWeight: 600,
      fontSize: '0.95rem',
      background: 'transparent',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },

    // Stats Cards Section
    statsSection: {
      padding: '60px 20px',
      background: '#f8fafc'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2rem'
    },
    statCard: {
      background: 'white',
      borderRadius: '24px',
      padding: '2rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem'
    },
    statIcon: {
      width: '60px',
      height: '60px',
      background: 'rgba(37,99,235,0.1)',
      borderRadius: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    statNumber: {
      fontSize: '2.2rem',
      fontWeight: 800,
      color: '#2563eb',
      margin: 0,
      lineHeight: 1
    },
    statCardLabel: {
      color: '#64748b',
      fontWeight: 500,
      margin: '0.5rem 0 0',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },

    // Booking Section
    bookingSection: {
      padding: '80px 20px',
      background: 'white'
    },
    sectionTag: {
      display: 'inlineBlock',
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '3px',
      padding: '0.4rem 1.2rem',
      background: 'rgba(37,99,235,0.1)',
      color: '#2563eb',
      borderRadius: '100px',
      marginBottom: '1rem'
    },
    sectionTitle: {
      fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
      fontWeight: 800,
      marginBottom: '1rem',
      color: '#0f172a'
    },
    sectionSubtitle: {
      color: '#64748b',
      fontSize: '1rem',
      maxWidth: '700px',
      margin: '0 auto 3rem',
      textAlign: 'center'
    },
    bookingGrid: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: '2.5rem'
    },
    bookingCard: {
      background: 'white',
      borderRadius: '32px',
      padding: '3rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    },
    formSection: {
      marginBottom: '2.5rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e2e8f0'
    },
    sectionTitleSmall: {
      fontSize: '1.1rem',
      fontWeight: 700,
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem',
      color: '#0f172a',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    sectionIcon: {
      width: '32px',
      height: '32px',
      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '1rem'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem'
    },
    formGroup: {
      width: '100%',
      marginBottom: '1rem'
    },
    label: {
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      display: 'block'
    },
    requiredStar: {
      color: '#ef4444',
      marginLeft: '0.2rem'
    },
    input: {
      width: '100%',
      padding: '0.9rem 1.2rem',
      borderRadius: '12px',
      border: '2px solid #e2e8f0',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease'
    },
    serviceGrid: {
      display: 'grid',
     gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '1rem',
      marginTop: '1rem'
    },
    serviceCard: {
      background: '#f8fafc',
      border: '2px solid transparent',
      borderRadius: '20px',
      padding: '1.2rem',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    serviceCardActive: {
      borderColor: '#2563eb',
      background: 'rgba(37,99,235,0.1)',
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 20px rgba(37,99,235,0.2)'
    },
    serviceIcon: {
      fontSize: '1.8rem',
      color: '#2563eb',
      marginBottom: '0.5rem'
    },
    serviceLabel: {
      fontWeight: 600,
      fontSize: '0.85rem',
      color: '#0f172a',
      display: 'block'
    },
    timelineGrid: {
      display: 'flex',
      gap: '1rem',
      marginTop: '0.5rem'
    },
    timelineOption: {
      flex: 1
    },
    timelineLabel: {
      display: 'block',
      textAlign: 'center',
      padding: '1rem',
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.9rem',
      transition: 'all 0.3s ease'
    },
    timelineLabelActive: {
      borderColor: '#2563eb',
      background: '#2563eb',
      color: 'white'
    },
    cateringGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem'
    },
    cateringCard: {
      position: 'relative'
    },
    cateringLabel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1.5rem 1rem',
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '20px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    cateringLabelActive: {
      borderColor: '#2563eb',
      background: '#2563eb',
      color: 'white'
    },
    cateringIcon: {
      fontSize: '2rem',
      color: '#2563eb'
    },
    venueGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '0.8rem'
    },
    venueItem: {
      position: 'relative'
    },
    venueLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '1rem',
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: '0.85rem',
      transition: 'all 0.3s ease'
    },
    venueLabelActive: {
      borderColor: '#2563eb',
      background: '#2563eb',
      color: 'white'
    },
    venueIcon: {
      color: '#2563eb',
      fontSize: '1rem'
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.8rem'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    checkboxLabel: {
      fontSize: '0.95rem',
      color: '#334155',
      cursor: 'pointer'
    },
    submitButton: {
      background: '#25D366',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '1rem 3rem',
      fontWeight: 600,
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 20px rgba(37,211,102,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      width: '100%'
    },
    secureText: {
      color: '#64748b',
      fontSize: '0.85rem',
      marginTop: '1rem',
      textAlign: 'center'
    },
    alertSuccess: {
      background: '#10b981',
      color: 'white',
      padding: '1rem',
      borderRadius: '12px',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    },
    alertError: {
      background: '#ef4444',
      color: 'white',
      padding: '1rem',
      borderRadius: '12px',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    },
    alertClose: {
      position: 'absolute',
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: '1.5rem',
      cursor: 'pointer'
    },

    // Sidebar
    sidebarCard: {
      background: 'white',
      borderRadius: '28px',
      padding: '2.2rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      marginBottom: '2rem',
      border: '1px solid #e2e8f0'
    },
    sidebarTitle: {
      fontSize: '1.3rem',
      fontWeight: 700,
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem',
      color: '#0f172a'
    },
    sidebarIcon: {
      width: '40px',
      height: '40px',
      background: 'rgba(37,99,235,0.1)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#2563eb'
    },
    benefitList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    benefitItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem',
      fontWeight: 500,
      color: '#0f172a'
    },
    benefitIcon: {
      width: '24px',
      height: '24px',
      background: '#10b981',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '0.8rem'
    },
    availabilityBadge: {
      background: '#10b981',
      color: 'white',
      padding: '0.8rem 1.2rem',
      borderRadius: '16px',
      fontSize: '0.9rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem'
    },
    pulseDot: {
      width: '10px',
      height: '10px',
      background: 'white',
      borderRadius: '50%',
      animation: 'pulse 2s infinite'
    },
    testimonialCard: {
      background: '#f8fafc',
      borderRadius: '20px',
      padding: '1.5rem',
      marginBottom: '1rem'
    },
    testimonialText: {
      fontSize: '0.95rem',
      fontStyle: 'italic',
      color: '#334155',
      marginBottom: '1rem'
    },
    testimonialAuthor: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    testimonialImage: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      objectFit: 'cover'
    },
    testimonialName: {
      fontSize: '1rem',
      fontWeight: 700,
      margin: 0
    },
    testimonialTitle: {
      fontSize: '0.8rem',
      color: '#64748b'
    },
    contactButton: {
      display: 'block',
      textDecoration: 'none',
      padding: '1rem',
      borderRadius: '12px',
      fontWeight: 600,
      textAlign: 'center',
      marginBottom: '1rem',
      transition: 'all 0.3s ease'
    },
    contactButtonPrimary: {
      background: '#2563eb',
      color: 'white'
    },
    contactButtonOutline: {
      border: '2px solid #2563eb',
      color: '#0f172a'
    },

    // Features Section
    featuresSection: {
      padding: '80px 20px',
      background: '#f8fafc'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '2rem'
    },
    featureCard: {
      background: 'white',
      borderRadius: '28px',
      padding: '2.5rem 2rem',
      textAlign: 'center',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
    },
    featureIcon: {
      width: '80px',
      height: '80px',
      background: '#2563eb',
      borderRadius: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      color: 'white'
    },
    featureTitle: {
      fontSize: '1.2rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
      color: '#0f172a'
    },
    featureDesc: {
      color: '#64748b',
      fontSize: '0.9rem',
      margin: 0
    },

    // FAQ Section
    faqSection: {
      padding: '80px 20px',
      background: '#f8f9fa',
      color: '#333'
    },
    faqContainer: {
      maxWidth: '900px',
      margin: '0 auto'
    },
    faqItem: {
      background: 'white',
      borderRadius: '16px',
      marginBottom: '1rem',
      overflow: 'hidden',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      transition: 'all 0.3s ease',
      border: '1px solid #e9ecef'
    },
    faqQuestion: {
      padding: '1.5rem 2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: 'white'
    },
    faqQuestionText: {
      color: '#1a1a1a',
      fontSize: '1.1rem',
      fontWeight: 600,
      margin: 0
    },
    faqIcon: {
      width: '32px',
      height: '32px',
      background: '#2563eb',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      transition: 'all 0.3s ease'
    },
    faqAnswer: {
      maxHeight: '0',
      opacity: 0,
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      padding: '0 2rem',
      backgroundColor: '#f8f9fa'
    },
    faqAnswerShow: {
      maxHeight: '300px',
      opacity: 1,
      padding: '0 2rem 1.5rem 2rem'
    },
    faqAnswerText: {
      color: '#4a5568',
      margin: 0,
      lineHeight: 1.7,
      fontSize: '1rem'
    },

    // Trust Badges
    trustBadges: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2rem',
      marginTop: '3rem',
      flexWrap: 'wrap'
    },
    trustBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem',
      padding: '0.8rem 1.5rem',
      background: 'white',
      borderRadius: '100px',
      border: '1px solid #e9ecef',
      fontWeight: 500,
      color: '#4a5568',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    },

    // CTA Section
    ctaSection: {
      padding: '80px 20px',
      background: 'linear-gradient(135deg, #2563eb, #1e40af)',
      color: 'white',
      textAlign: 'center'
    },
    ctaTitle: {
      fontSize: 'clamp(1.8rem, 4vw, 3rem)',
      fontWeight: 700,
      marginBottom: '1rem',
      color: 'white'
    },
    ctaText: {
      fontSize: '1.2rem',
      marginBottom: '2rem',
      opacity: 0.95
    },
    ctaButton: {
      background: 'white',
      color: '#2563eb',
      border: 'none',
      borderRadius: '16px',
      padding: '1.2rem 3rem',
      fontWeight: 700,
      fontSize: '1.1rem',
      textDecoration: 'none',
      display: 'inlineBlock',
      cursor: 'pointer',
      boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
    },

    // Back to Top
    backToTop: {
      position: 'fixed',
      bottom: '30px',
      right: '30px',
      width: '54px',
      height: '54px',
      background: '#2563eb',
      color: 'white',
      borderRadius: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      opacity: 0,
      visibility: 'hidden',
      transition: 'all 0.3s ease',
      zIndex: 99,
      border: 'none',
      boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
    },
    backToTopShow: {
      opacity: 1,
      visibility: 'visible'
    },

    // Utilities
    textWhite: { color: 'white' },
    textCenter: { textAlign: 'center' },
    mb4: { marginBottom: '2rem' },
    mt4: { marginTop: '2rem' }
  };
<style>
{`
@media (max-width: 992px) {
  .booking-responsive {
    display: block !important;
  }
}

@media (max-width: 768px) {

  /* Reduce border for mobile */
  .main-container-mobile {
    width: 96% !important;
    border-left: 15px solid #000 !important;
    border-right: 15px solid #000 !important;
  }

}
`}
</style>
  return (
    <div style={styles.pageContainer}>
      <Navbar />
      
      {/* Main Container with Black Borders */}
      <div style={styles.mainContainer}>
        {/* Booking Section */}
        <section id="booking" style={styles.bookingSection}>
          <div style={styles.container}>
            <div style={styles.textCenter}>
              <span style={styles.sectionTag}>✦ START YOUR JOURNEY</span>
              <h2 style={styles.sectionTitle}>Tell Us About Your Vision</h2>
              <p style={styles.sectionSubtitle}>Every great event starts with a conversation. Share your requirements and let's create magic.</p>
            </div>

            <div style={styles.bookingGrid}>
              {/* Left Column - Booking Form */}
              <div style={styles.bookingCard}>
                
                {formStatus.status === 'success' && (
                  <div style={styles.alertSuccess}>
                    <i className="bi bi-check-circle-fill" style={{ marginRight: '0.5rem' }}></i>
                    {formStatus.message}
                    <button style={styles.alertClose} onClick={() => setFormStatus({ status: '', message: '' })}>×</button>
                  </div>
                )}

                {formStatus.status === 'error' && (
                  <div style={styles.alertError}>
                    <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: '0.5rem' }}></i>
                    {formStatus.message}
                    <button style={styles.alertClose} onClick={() => setFormStatus({ status: '', message: '' })}>×</button>
                  </div>
                )}

                <div style={{ marginBottom: '2rem' }}>
                  <h2>Event Details</h2>
                  <p style={{ color: '#64748b' }}>Please fill in the information below to help us understand your requirements</p>
                </div>

                <form onSubmit={handleFormSubmit}>
                  {/* Contact Information */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-person-badge" style={styles.sectionIcon}></i>
                      Contact Information
                    </h3>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Full Name <span style={styles.requiredStar}>*</span></label>
                        <input 
                          type="text" 
                          style={styles.input}
                          name="customerName"
                          value={formData.customerName}
                          onChange={handleInputChange}
                          placeholder="John Doe" 
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Company Name <span style={styles.requiredStar}>*</span></label>
                        <input 
                          type="text" 
                          style={styles.input}
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="Company Inc." 
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email Address <span style={styles.requiredStar}>*</span></label>
                        <input 
                          type="email" 
                          style={styles.input}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@company.com" 
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Phone Number <span style={styles.requiredStar}>*</span></label>
                        <input 
                          type="tel" 
                          style={styles.input}
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="+91 98765 43210" 
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Designation</label>
                        <input 
                          type="text" 
                          style={styles.input}
                          name="designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          placeholder="Marketing Manager"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Event Information */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-calendar-event" style={styles.sectionIcon}></i>
                      Event Information
                    </h3>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Event Type <span style={styles.requiredStar}>*</span></label>
                        <select 
                          style={styles.input}
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" disabled>Select type</option>
                          <option value="Corporate Conference">Corporate Conference</option>
                          <option value="Product Launch">Product Launch</option>
                          <option value="Annual Gala">Annual Gala</option>
                          <option value="Board Retreat">Board Retreat</option>
                          <option value="Workshop">Workshop / Training</option>
                          <option value="Virtual Event">Virtual Event</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Expected Attendees <span style={styles.requiredStar}>*</span></label>
                        <select 
                          style={styles.input}
                          name="attendees"
                          value={formData.attendees}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" disabled>Select range</option>
                          <option value="1-50">1-50 attendees</option>
                          <option value="51-100">51-100 attendees</option>
                          <option value="101-250">101-250 attendees</option>
                          <option value="251-500">251-500 attendees</option>
                          <option value="500+">500+ attendees</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Preferred Date <span style={styles.requiredStar}>*</span></label>
                        <input 
                          type="date" 
                          style={styles.input}
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]} 
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Preferred City</label>
                        <select 
                          style={styles.input}
                          name="preferredCity"
                          value={formData.preferredCity}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled>Select city</option>
                          <option value="Hyderabad">Hyderabad</option>
                          <option value="Mumbai">Mumbai</option>
                          <option value="Delhi NCR">Delhi NCR</option>
                          <option value="Bengaluru">Bengaluru</option>
                          <option value="Chennai">Chennai</option>
                          <option value="Pune">Pune</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Services Required */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-grid-3x3-gap-fill" style={styles.sectionIcon}></i>
                      Services Required
                    </h3>
                    <label style={styles.label}>Select the services you need:</label>
                    <div style={styles.serviceGrid}>
                      {servicesList.map((service, index) => (
                        <div 
                          key={index}
                          style={{
                            ...styles.serviceCard,
                            ...(selectedServices.includes(service.value) ? styles.serviceCardActive : {})
                          }}
                          onClick={() => toggleService(service.value)}
                        >
                          <i className={`bi ${service.icon}`} style={styles.serviceIcon}></i>
                          <span style={styles.serviceLabel}>{service.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Event Timeline */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-clock" style={styles.sectionIcon}></i>
                      Event Timeline
                    </h3>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Event Duration</label>
                        <select 
                          style={styles.input}
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                        >
                          <option value="Half Day">Half Day (4-5 hours)</option>
                          <option value="Full Day">Full Day (8-10 hours)</option>
                          <option value="Multi Day">Multi Day (2+ days)</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Preferred Time</label>
                        <div style={styles.timelineGrid}>
                          {['Morning', 'Afternoon', 'Evening'].map((time) => (
                            <div key={time} style={styles.timelineOption}>
                              <input 
                                type="radio" 
                                name="timeOfDay" 
                                value={time} 
                                id={time.toLowerCase()}
                                checked={formData.timeOfDay === time}
                                onChange={handleInputChange}
                                style={{ display: 'none' }}
                              />
                              <label 
                                htmlFor={time.toLowerCase()}
                                style={{
                                  ...styles.timelineLabel,
                                  ...(formData.timeOfDay === time ? styles.timelineLabelActive : {})
                                }}
                              >
                                {time}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Catering Preferences */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-egg-fried" style={styles.sectionIcon}></i>
                      Catering Preferences
                    </h3>
                    <div style={styles.cateringGrid}>
                      {[
                        { value: 'Vegetarian', icon: 'bi-flower1', label: 'Vegetarian' },
                        { value: 'Non-Vegetarian', icon: 'bi-egg-fried', label: 'Non-Vegetarian' },
                        { value: 'Both', icon: 'bi-brightness-alt-high', label: 'Both' }
                      ].map((item) => (
                        <div key={item.value} style={styles.cateringCard}>
                          <input 
                            type="radio" 
                            name="catering" 
                            value={item.value} 
                            id={item.value}
                            checked={formData.catering === item.value}
                            onChange={handleInputChange}
                            style={{ display: 'none' }}
                          />
                          <label 
                            htmlFor={item.value}
                            style={{
                              ...styles.cateringLabel,
                              ...(formData.catering === item.value ? styles.cateringLabelActive : {})
                            }}
                          >
                            <i className={`bi ${item.icon}`} style={formData.catering === item.value ? { color: 'white' } : styles.cateringIcon}></i>
                            <span>{item.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <input 
                        type="text" 
                        style={styles.input}
                        name="dietary"
                        value={formData.dietary}
                        onChange={handleInputChange}
                        placeholder="Dietary restrictions (e.g., Jain, Vegan, Gluten-free)"
                      />
                    </div>
                  </div>

                  {/* Venue Preferences */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-building" style={styles.sectionIcon}></i>
                      Venue Preferences
                    </h3>
                    <div style={styles.venueGrid}>
                      {[
                        { value: 'Indoor', icon: 'bi-building', label: 'Indoor' },
                        { value: 'Outdoor', icon: 'bi-sun', label: 'Outdoor' },
                        { value: 'Resort', icon: 'bi-tree', label: 'Resort' },
                        { value: 'Hotel', icon: 'bi-building', label: 'Hotel' },
                        { value: 'Convention', icon: 'bi-diagram-3', label: 'Convention' },
                        { value: 'Beachfront', icon: 'bi-water', label: 'Beachfront' }
                      ].map((item) => (
                        <div key={item.value} style={styles.venueItem}>
                          <input 
                            type="checkbox" 
                            name="venuePref"
                            value={item.value} 
                            id={item.value}
                            checked={formData.venuePref.includes(item.value)}
                            onChange={handleInputChange}
                            style={{ display: 'none' }}
                          />
                          <label 
                            htmlFor={item.value}
                            style={{
                              ...styles.venueLabel,
                              ...(formData.venuePref.includes(item.value) ? styles.venueLabelActive : {})
                            }}
                          >
                            <i className={`bi ${item.icon}`} style={formData.venuePref.includes(item.value) ? { color: 'white' } : styles.venueIcon}></i>
                            <span>{item.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Event Requirements */}
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitleSmall}>
                      <i className="bi bi-chat-dots" style={styles.sectionIcon}></i>
                      Event Requirements
                    </h3>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={styles.label}>Tell us about your event <span style={styles.requiredStar}>*</span></label>
                      <textarea 
                        style={{...styles.input, minHeight: '120px'}}
                        name="requirements"
                        value={formData.requirements}
                        onChange={handleInputChange}
                        placeholder="Please describe your event vision, goals, theme, and any specific requirements..." 
                        required
                      />
                    </div>
                    <div>
                      <label style={styles.label}>How did you hear about us?</label>
                      <select 
                        style={styles.input}
                        name="source"
                        value={formData.source}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled>Select option</option>
                        <option value="Google">Google Search</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Referral">Referral</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Previous Event">Previous Event</option>
                      </select>
                    </div>
                  </div>

                  {/* Terms & Consent */}
                  <div style={styles.formSection}>
                    <div style={styles.checkboxGroup}>
                      <input 
                        type="checkbox" 
                        name="consent" 
                        id="consent"
                        checked={formData.consent}
                        onChange={handleInputChange}
                        required
                        style={styles.checkbox}
                      />
                      <label htmlFor="consent" style={styles.checkboxLabel}>
                        I agree to the processing of my data and wish to be contacted <span style={styles.requiredStar}>*</span>
                      </label>
                    </div>
                    <div style={styles.checkboxGroup}>
                      <input 
                        type="checkbox" 
                        name="updates" 
                        id="updates"
                        checked={formData.updates}
                        onChange={handleInputChange}
                        style={styles.checkbox}
                      />
                      <label htmlFor="updates" style={styles.checkboxLabel}>
                        I'd like to receive updates about events and offers
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={styles.textCenter}>
                    <button type="submit" style={styles.submitButton}>
                      <i className="bi bi-whatsapp" style={{ marginRight: '0.5rem' }}></i>
                      Send via WhatsApp
                    </button>
                    <p style={styles.secureText}>
                      <i className="bi bi-shield-check" style={{ marginRight: '0.3rem' }}></i>
                      Your information will be sent securely via WhatsApp
                    </p>
                  </div>
                </form>
              </div>

              {/* Right Column - Sidebar */}
              <div>
                {/* Why Choose Us Card */}
                <div style={styles.sidebarCard}>
                  <h4 style={styles.sidebarTitle}>
                    <i className="bi bi-star-fill" style={styles.sidebarIcon}></i>
                    Why Choose MEDIAWAGON
                  </h4>
                  <ul style={styles.benefitList}>
                    {[
                      '8+ Years of Excellence',
                      '500+ Successful Events',
                      'ISO 9001:2015 Certified',
                      'Fortune 500 Clients',
                      '50+ Global Partners',
                      'Award-Winning Team'
                    ].map((item, index) => (
                      <li key={index} style={styles.benefitItem}>
                        <i className="bi bi-check-lg" style={styles.benefitIcon}></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div style={styles.availabilityBadge}>
                    <div style={styles.pulseDot}></div>
                    <span>Team Available Now - Avg Response 2hrs</span>
                  </div>
                </div>

                {/* Testimonials Card */}
                <div style={styles.sidebarCard}>
                  <h4 style={styles.sidebarTitle}>
                    <i className="bi bi-chat-quote" style={styles.sidebarIcon}></i>
                    Client Testimonials
                  </h4>
                  
                  {testimonials.map((testimonial, index) => (
                    <div key={index} style={styles.testimonialCard}>
                      <p style={styles.testimonialText}>"{testimonial.text}"</p>
                      <div style={styles.testimonialAuthor}>
                        <img src={testimonial.image} alt={testimonial.name} style={styles.testimonialImage} />
                        <div>
                          <h6 style={styles.testimonialName}>{testimonial.name}</h6>
                          <span style={styles.testimonialTitle}>{testimonial.title}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Contact Card */}
                <div style={styles.sidebarCard}>
                  <h4 style={styles.sidebarTitle}>
                    <i className="bi bi-telephone" style={styles.sidebarIcon}></i>
                    Need Immediate Assistance?
                  </h4>
                  <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>Our premium support team is available 24/7 for urgent inquiries</p>
                  <a href="tel:+918688365247" style={{...styles.contactButton, ...styles.contactButtonPrimary}}>
                    <i className="bi bi-telephone" style={{ marginRight: '0.5rem' }}></i>+91 86883 65247
                  </a>
                  <a href="mailto:hello@mediawagon.com" style={{...styles.contactButton, ...styles.contactButtonOutline}}>
                    <i className="bi bi-envelope" style={{ marginRight: '0.5rem' }}></i>hello@mediawagon.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section style={styles.featuresSection}>
          <div style={styles.container}>
            <div style={styles.textCenter}>
              <span style={styles.sectionTag}>✦ PREMIUM FEATURES</span>
              <h2 style={styles.sectionTitle}>The MEDIAWAGON Advantage</h2>
              <p style={styles.sectionSubtitle}>Experience excellence at every step of your event journey</p>
            </div>

            <div style={styles.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} style={styles.featureCard}>
                  <div style={styles.featureIcon}>
                    <i className={`bi ${feature.icon}`} style={{ fontSize: '2.5rem', color: 'white' }}></i>
                  </div>
                  <h5 style={styles.featureTitle}>{feature.title}</h5>
                  <p style={styles.featureDesc}>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Stats Cards */}
        <section style={styles.statsSection}>
          <div style={styles.container}>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statIcon}>
                  <i className="bi bi-calendar-check" style={{ fontSize: '2rem', color: '#2563eb' }}></i>
                </div>
                <div>
                  <h3 style={styles.statNumber}>500+</h3>
                  <p style={styles.statCardLabel}>Events Delivered</p>
                </div>
              </div>
              
              <div style={styles.statCard}>
                <div style={styles.statIcon}>
                  <i className="bi bi-people" style={{ fontSize: '2rem', color: '#2563eb' }}></i>
                </div>
                <div>
                  <h3 style={styles.statNumber}>98%</h3>
                  <p style={styles.statCardLabel}>Satisfaction Rate</p>
                </div>
              </div>
              
              <div style={styles.statCard}>
                <div style={styles.statIcon}>
                  <i className="bi bi-clock-history" style={{ fontSize: '2rem', color: '#2563eb' }}></i>
                </div>
                <div>
                  <h3 style={styles.statNumber}>24/7</h3>
                  <p style={styles.statCardLabel}>Premium Support</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section id="faq" style={styles.faqSection}>
          <div style={styles.container}>
            <div style={styles.textCenter}>
              <span style={{...styles.sectionTag, background: '#e9ecef', color: '#2563eb'}}>✦ FAQ</span>
              <h2 style={{...styles.sectionTitle, color: '#1a1a1a'}}>Frequently Asked Questions</h2>
              <p style={{ color: '#4a5568', marginBottom: '3rem' }}>Find answers to common questions about booking with us</p>
            </div>

            <div style={styles.faqContainer}>
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  style={{
                    ...styles.faqItem,
                    ...(activeFaq === index ? { boxShadow: '0 8px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' } : {})
                  }}
                  onClick={() => toggleFaq(index)}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = activeFaq === index ? 'translateY(-2px)' : 'translateY(0)'}
                >
                  <div style={styles.faqQuestion}>
                    <h5 style={styles.faqQuestionText}>{faq.question}</h5>
                    <div style={{
                      ...styles.faqIcon,
                      ...(activeFaq === index ? { transform: 'rotate(180deg)' } : {})
                    }}>
                      <i className={`bi ${activeFaq === index ? 'bi-dash' : 'bi-plus'}`} style={{ fontSize: '1.2rem' }}></i>
                    </div>
                  </div>
                  <div style={{
                    ...styles.faqAnswer,
                    ...(activeFaq === index ? styles.faqAnswerShow : {})
                  }}>
                    <p style={styles.faqAnswerText}>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div style={styles.trustBadges}>
              {[
                { icon: 'bi-shield-check', text: 'ISO 9001:2015 Certified' },
                { icon: 'bi-lock', text: 'Secure & Confidential' },
                { icon: 'bi-clock', text: '24/7 Premium Support' }
              ].map((badge, index) => (
                <div key={index} style={styles.trustBadge}>
                  <i className={`bi ${badge.icon}`} style={{ color: '#2563eb' }}></i>
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Back to Top Button */}
      <div 
        style={{
          ...styles.backToTop,
          ...(showBackToTop ? styles.backToTopShow : {})
        }}
        onClick={handleBackToTop}
      >
        <i className="bi bi-arrow-up"></i>
      </div>
    </div>
  );
};

export default BookingServices;